package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.takeOrElse
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.atoms.inputs.InputStyles
import com.patronat.top.ui.theme.InputPlaceholderText
import com.patronat.top.ui.theme.InputTextContent
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White

@Composable
fun TimeInputField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "hh:mm",
    enabled: Boolean = true,
    readOnly: Boolean = false,
    textStyle: TextStyle = InputTextContent,
    placeholderTextStyle: TextStyle = InputPlaceholderText,
    fieldHeight: Dp = 38.dp,
    keyboardOptions: KeyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
    keyboardActions: KeyboardActions = KeyboardActions.Default
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    val currentState = when {
        !enabled -> InputState.Disabled
        readOnly -> InputState.ReadOnly
        isFocused -> InputState.Active
        value.isNotEmpty() -> InputState.Filled
        else -> InputState.Default
    }

    val styles = getTimeInputStyles(
        state = currentState,
        inputTextStyle = textStyle
    )

    BasicTextField(
        value = value,
        onValueChange = { newValue ->
            val formatted = formatTimeInput(newValue)
            if (formatted != value) {
                onValueChange(formatted)
            }
        },
        enabled = enabled,
        readOnly = readOnly,
        textStyle = styles.textColor.copy(textAlign = TextAlign.Start),
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        interactionSource = interactionSource,
        singleLine = true,
        cursorBrush = SolidColor(Primary600),
        modifier = modifier,
        decorationBox = { innerTextField ->
            Box(
                modifier = Modifier
                    .height(fieldHeight)
                    .fillMaxWidth()
                    .background(styles.backgroundColor, RoundedCornerShape(8.dp))
                    .border(styles.borderWidth, styles.borderColor, RoundedCornerShape(8.dp))
                    .padding(start = 16.dp, top = 4.dp, end = 16.dp, bottom = 4.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                if (value.isEmpty()) {
                    Text(
                        text = placeholder,
                        style = placeholderTextStyle.copy(textAlign = TextAlign.Start),
                        color = placeholderTextStyle.color.takeOrElse { styles.placeholderColor }
                    )
                }
                innerTextField()
            }
        }
    )
}

private fun formatTimeInput(input: String): String {
    val digits = input.filter { it.isDigit() }.take(4)
    val result = StringBuilder()
    digits.forEachIndexed { index, digit ->
        if (index == 2) {
            result.append(':')
        }
        result.append(digit)
    }
    return result.toString()
}

@Composable
private fun getTimeInputStyles(
    state: InputState,
    inputTextStyle: TextStyle
): InputStyles {
    val baseTextStyle = inputTextStyle.copy(textAlign = TextAlign.Center)

    return when (state) {
        InputState.Default -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft900,
            iconColor = Primary600
        )
        InputState.Active -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft900,
            iconColor = Primary600
        )
        InputState.Filled -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft900,
            iconColor = MineShaft900
        )
        InputState.ReadOnly -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = MineShaft900
        )
        InputState.Error -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft900,
            iconColor = MineShaft900
        )
        InputState.Success -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(color = MineShaft900),
            placeholderColor = MineShaft900,
            iconColor = MineShaft900
        )
        InputState.Disabled -> InputStyles(
            borderColor = MineShaft100,
            borderWidth = 0.5.dp,
            backgroundColor = MineShaft100,
            textColor = baseTextStyle.copy(color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = Primary600
        )
    }
}
